import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import './Payment.scss';
import { debounce, formatAccountType, getExtraServiseType, getSearchParamValue, getErrorMessage } from '../../utils';
import { AccountTypes, GlobalRouteNames, LocalStorageItems, QueryParams } from '../../models/Common';
import { ServicesTypes } from '../../models/Accounts';

import {
  configMaxCountMonth,
  configInitCountMonth,
  ExtraServiceKey,
  ExtraServicesMap,
  PaymentRoutes,
} from '../../constants/configuration';
import { Page404 } from '../404';
import { ApiError } from '../../models/ApiError';
import { OrderParams, PreparedCalculateData } from '../../models/Calculate';
import { paymentsApi } from '../../services/apiServices/user/payments';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { initialState } from '../../store/reducers/UserInfoSlice';
import { useAppSelector } from '../../hooks/redux';
import { useUserAddress } from '../../hooks/useUserAddress';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { DateVOv2 } from '../../core/DateVOv2';
import { IMultiAccount, IUserInfoTransform } from '../../models/UserInfo';
import { FormToggleButtonRefactor } from '../../components/inputs/FormToggleButton/FormToggleButtonRefactor';
import { RefactorInfoBlock } from '../../components/servicePaymentParts/RefactorInfoBlock';
import PaymentTitle from '../../components/servicePaymentParts/PaymentTitle/PaymentTitle';
import ServiceInfo from '../../components/servicePaymentParts/ServiceInfo/ServiceInfo';
import ExtraservicesBlock from '../../components/servicePaymentParts/ExtraservicesBlock/ExtraservicesBlock';
import { ServiceEx, TransformServicePreset } from '../../models/ServicePresets';
import MonthRange from '../../components/servicePaymentParts/MonthRange/MonthRange';
import ServicePaymentFormRefactor from '../../components/servicePaymentParts/ServicePaymentFormRefactor/ServicePaymentFormRefactor';

export type InfoBlockType = 'now' | 'hasPrepaid' | 'notPrepaid' | 'daily' | null;

export interface ChangeCurrentServiceProps {
  isFilmsChanged: boolean;
  isIpChanged: boolean;
}

export interface ServicePaymentFormState extends OrderParams {
  servicesExtra: {
    [key in ExtraServiceKey]: boolean;
  };
}

interface IViewState {
  isMonthRangeVisible: boolean;
  monthToggle: string[];
  typeInfoBlock: InfoBlockType;
  isShowBonusCheckbox: boolean;
  maxCountMoth: number;
}

export const PaymentRefactor: FC = () => {
  const { search } = useLocation();
  const history = useHistory();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const accountType = formatAccountType(getSearchParamValue(QueryParams.AccountType, search));
  const accountId = getSearchParamValue(QueryParams.AccountId, search);
  const counts = +getSearchParamValue(QueryParams.Counts, search);
  const serviceForPaymentId = +getSearchParamValue(QueryParams.ServiceId, search);
  const isChangeService = getSearchParamValue(QueryParams.IsChangeService, search) === 'true';
  const isConnectStaticIP = getSearchParamValue(QueryParams.ConnectStaticIp, search) === 'true';
  const isConnectFilms = getSearchParamValue(QueryParams.ConnectFilms, search) === 'true';
  const isChangeFilms = useRef(false);
  const isChangeStaticIp = useRef(false);
  const controlledAccount = useMemo(() => {
    const acc = localStorage.getItem(LocalStorageItems.ControlledAccount);
    return acc ? JSON.parse(acc) : null;
  }, []) as IMultiAccount | null;

  const { data: userInfoState } = userInfoApi.useGetUserInfoMultiAccWithTransformResponseQuery({
    subAccountId: controlledAccount?.UserID,
  });
  const { Permissions, Shares: userInfoShares, сonnectedShare50Percent } = userInfoState ?? {};

  const {
    data: init,
    isError: isInitError,
    error: initError,
  } = paymentsApi.useGetServicePresetsWithTransformResponseQuery(
    { accountId, accountType, serviceForPaymentId },
    { skip: !accountType },
  );
  const { userAddress } = useUserAddress(userInfoState || initialState.userInfoState, selectedLanguage);

  const {
    ChangeCurrentServiceInfo,
    // serviceForPayment, internetLogin, isPaidServiceSuspension получаем из модифицированного ответа
    serviceForPayment,
    internetLogin,
    isPaidServiceSuspension,
    ServicesEx,
    isBonusPrepaidsAvailable,
  } = init ?? {};

  const [calculateCostAPI, { isLoading: isLoadingCalculation, isError: calculateError }] =
    paymentsApi.useCalculateCostWithTransformResponseMutation();

  const [viewState, setViewState] = useState<IViewState>({
    isMonthRangeVisible: true,
    monthToggle: [],
    typeInfoBlock: null,
    isShowBonusCheckbox: true,
    maxCountMoth: counts || configInitCountMonth,
  });

  const methods = useForm<ServicePaymentFormState>({
    defaultValues: {
      accountType,
      accountId,
      serviceId: serviceForPaymentId,
      ServiceExIDs: accountType === AccountTypes.Union ? { inet: [], iptv: [], ctv: [] } : [],
      dateFrom: new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy'),
      count: counts || configInitCountMonth,
      isBonusPayment: false,
      changeCurrentService: isChangeService,
      servicesExtra: {
        films: !!isConnectFilms,
        dedicatedIp: !!isConnectStaticIP,
      },
    },
  });

  const [calculation, setCalculation] = useState<null | PreparedCalculateData>(null);

  const updateCalculation = useMemo(
    () =>
      debounce(async () => {
        try {
          const params = methods.getValues();
          const res = await calculateCostAPI({ ...params, count: params.count || 1 }).unwrap();
          setCalculation(res);
        } catch (error) {
          const message = getErrorMessage(error);
          customEnqueueSnackbar(message, 'error');
        }
      }, 400),
    [calculateCostAPI, customEnqueueSnackbar, methods],
  );

  const setIsChangeServicesForCalculation = useCallback(
    ({ initService }: { initService: TransformServicePreset }) => {
      const isToday = new DateVOv2({ dateString: methods.getValues().dateFrom || '', format: 'dd-MM-yyyy' })
        .getLuxonKiev()
        .hasSame(new DateVOv2().getLuxonKiev(), 'day');
      const isChanging =
        initService.StatusID === 1 && isToday && (isChangeService || isChangeStaticIp.current || isChangeFilms.current);
      methods.setValue('changeCurrentService', isChanging);
    },
    [isChangeService, methods],
  );

  const setDateFromForCalculation = useCallback(
    ({ initService }: { initService: TransformServicePreset }) => {
      const initialDateFromInit = new DateVOv2({
        dateString: initService.FromDate,
        format: 'yyyy-MM-dd HH:mm:ss',
      }).getLuxonKiev();

      const today = new DateVOv2().getLuxonKiev();
      const baseDate = initialDateFromInit.toFormat('dd-MM-yyyy');
      let date = baseDate;
      const dedicatedIpService = initService.ServicesEx.find((i) =>
        i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.dedicatedIp),
      );

      const filmsService = initService.ServicesEx.find((i) =>
        i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.films),
      );

      if (
        ((isChangeFilms.current && !filmsService?.IsConnected) ||
          (isChangeStaticIp.current && !dedicatedIpService?.IsConnected) ||
          isChangeService) &&
        initService.StatusID === 1
      ) {
        date = today.toFormat('dd-MM-yyyy');
      }

      methods.setValue('dateFrom', date);
    },
    [isChangeService, methods],
  );

  const getMonthToggle = useCallback(
    ({ initService }: { initService: TransformServicePreset }) => {
      const isActivated = initService.StatusID === 1 || !!initService.Prepaids?.length;
      const isOneBtn =
        isActivated ||
        initService.IsDisabled ||
        [ServicesTypes.INTERNET_TV, ServicesTypes.INTERNET_TV_MAX].includes(
          initService.serviceForPayment?.Type?.id || 0,
        ) ||
        accountType === AccountTypes.Ctv;

      const dateFrom = new DateVOv2({ dateString: initService.FromDate, format: 'yyyy-MM-dd HH:mm:ss' }).getLuxonKiev();
      const today = new DateVOv2().getLuxonKiev();
      const nextMonth = dateFrom.startOf('month').plus({ month: 1 });

      const baseToggle = isOneBtn
        ? [dateFrom.toFormat('dd-MM-yyyy')]
        : [dateFrom.toFormat('dd-MM-yyyy'), nextMonth.toFormat('dd-MM-yyyy')];

      const hasChange =
        (isChangeFilms.current || isChangeStaticIp.current || isChangeService) &&
        initService.StatusID === 1 &&
        !initService.CurrentService.Daily;

      return hasChange ? [today.toFormat('dd-MM-yyyy'), dateFrom.toFormat('dd-MM-yyyy')] : baseToggle;
    },
    [accountType, isChangeService],
  );

  const getIsMonthRangeVisible = useCallback(
    ({ initService }: { initService: TransformServicePreset }) => {
      const today = new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy');
      const isDateActivationEqualToday = methods.getValues().dateFrom === today;
      const isServiceActiveToday = initService.StatusID === 1;
      const isTodayActive = isDateActivationEqualToday && isServiceActiveToday;

      const isChangesInServiceNoEdit =
        !initService.CanChangeCurrentService && (!isChangeService || isChangeFilms.current || isChangeStaticIp.current);

      if (
        isTodayActive &&
        ((!isChangeService &&
          (isChangeFilms.current ||
            isChangeStaticIp.current ||
            (isChangesInServiceNoEdit && !initService.Prepaids?.length))) ||
          (isChangeService && !initService.CanChangeCurrentService))
      ) {
        return false;
      }
      return true;
    },
    [isChangeService, methods],
  );

  const getTypeInfoBlock = useCallback(
    ({ initService }: { initService: TransformServicePreset }): InfoBlockType => {
      const today = new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy');
      const isDateActivationEqualToday = methods.getValues().dateFrom === today;
      const isServiceActiveToday = initService.StatusID === 1;
      const dedicatedIpService = initService.ServicesEx.find((i) =>
        i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.dedicatedIp),
      );

      const filmsService = initService.ServicesEx.find((i) =>
        i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.films),
      );
      const isStaticIpRemoved = dedicatedIpService?.IsConnected && isChangeStaticIp.current;
      const isFilmsRemoved = filmsService?.IsConnected && isChangeFilms.current;
      const hasRemovals = isStaticIpRemoved || isFilmsRemoved;
      const hasPrepaids = Boolean(initService.Prepaids?.length);
      const isChangeToday = isDateActivationEqualToday && isChangeService;

      if (initService.CanChangeCurrentService && isChangeToday && isServiceActiveToday) {
        return 'now';
      }

      if (isDateActivationEqualToday && hasRemovals) {
        return 'hasPrepaid';
      }

      if (!initService.CanChangeCurrentService && isChangeToday && isServiceActiveToday) {
        return hasRemovals || hasPrepaids ? 'hasPrepaid' : 'notPrepaid';
      }

      return null;
    },
    [isChangeService, methods],
  );

  const getIsShowBonusCheckBox = useCallback(
    ({
      initService,
      userInfo,
      typeInfoBlock,
    }: {
      initService: TransformServicePreset;
      userInfo: IUserInfoTransform;
      typeInfoBlock: InfoBlockType;
    }) => {
      const today = new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy');
      const isDateActivationEqualToday = methods.getValues().dateFrom === today;

      return (
        !(isChangeService && isDateActivationEqualToday) &&
        typeInfoBlock !== 'hasPrepaid' &&
        typeInfoBlock !== 'notPrepaid' &&
        userInfo.Permissions?.Info?.ShowBonuses &&
        accountType !== 'ctv' &&
        (!initService.isPaidServiceSuspension ||
          (initService.isPaidServiceSuspension &&
            (methods.getValues('servicesExtra.dedicatedIp') || methods.getValues('servicesExtra.films'))))
      );
    },
    [accountType, isChangeService, methods],
  );

  const getMaxCountMonth = useCallback(
    ({ initService, userInfo }: { initService: TransformServicePreset; userInfo: IUserInfoTransform }) => {
      if (initService.isPaidServiceSuspension) {
        return initService.CountSuspendPayments;
      }
      if (!userInfo.сonnectedShare50Percent) {
        return configMaxCountMonth;
      }
      const startMonth = new DateVOv2().getLuxonKiev().startOf('month');
      const today = new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy');
      const isDateActivationEqualToday = methods.getValues().dateFrom === today;

      const initDate =
        !initService.FromDate || (isChangeService && isDateActivationEqualToday)
          ? startMonth
          : new DateVOv2({ dateString: initService.FromDate, format: 'yyyy-MM-dd HH:mm:ss' })
              .getLuxonKiev()
              .startOf('month');

      const expireDate = DateTime.fromSQL(userInfo.сonnectedShare50Percent.ActiveTo)
        .setZone('Europe/Kiev')
        .startOf('month');
      const diffDays = Math.round(expireDate.diff(initDate, 'month').as('month') + 1);

      if (diffDays > 0) {
        return diffDays;
      }
      return configMaxCountMonth;
    },
    [isChangeService, methods],
  );

  const updateViewState = useCallback(
    ({ initService, userInfo }: { initService: TransformServicePreset; userInfo: IUserInfoTransform }) => {
      const typeInfoBlock = getTypeInfoBlock({ initService });
      const isShowBonusCheckbox = getIsShowBonusCheckBox({ initService, userInfo, typeInfoBlock });
      const isMonthRangeVisible = getIsMonthRangeVisible({ initService });
      const monthToggle = getMonthToggle({ initService });
      const maxCountMonth = getMaxCountMonth({ initService, userInfo });

      if (!isShowBonusCheckbox) methods.setValue('isBonusPayment', false);

      setViewState((prev) => ({
        ...prev,
        isShowBonusCheckbox,
        typeInfoBlock,
        isMonthRangeVisible,
        monthToggle,
        maxCountMoth: maxCountMonth,
      }));
    },
    [getIsMonthRangeVisible, getIsShowBonusCheckBox, getMaxCountMonth, getMonthToggle, getTypeInfoBlock, methods],
  );

  const initialize = useCallback(
    ({ initService, userInfo }: { initService: TransformServicePreset; userInfo: IUserInfoTransform }) => {
      initService.ServicesEx.forEach((i) => {
        const type = getExtraServiseType(i.Tags);
        if (type) {
          methods.setValue(`servicesExtra.${type}`, i.IsConnected);
        }
      });

      methods.setValue('serviceId', initService.serviceForPayment.id);
      methods.setValue('ServiceExIDs', initService.ServiceExIDs);

      if (isConnectStaticIP) {
        isChangeStaticIp.current = true;
        const dedicatedIpService = initService.ServicesEx.find((i) =>
          i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.dedicatedIp),
        );

        const currentServiceExIds = methods.getValues().ServiceExIDs;
        if (Array.isArray(currentServiceExIds) && !dedicatedIpService?.ExIdsKey && dedicatedIpService) {
          methods.setValue('ServiceExIDs', [...currentServiceExIds, dedicatedIpService.id]);
        } else if (dedicatedIpService && dedicatedIpService.ExIdsKey && currentServiceExIds) {
          methods.setValue('ServiceExIDs', {
            ...currentServiceExIds,
            [dedicatedIpService.ExIdsKey]: [...currentServiceExIds[dedicatedIpService.ExIdsKey], dedicatedIpService.id],
          });
        }
        methods.setValue('servicesExtra.dedicatedIp', true);
      }

      if (isConnectFilms) {
        isChangeFilms.current = true;
        const filmsService = initService.ServicesEx.find((i) =>
          i.Tags.map((tag) => tag.id).includes(ExtraServicesMap.films),
        );
        const currentServiceExIds = methods.getValues().ServiceExIDs;
        if (Array.isArray(currentServiceExIds) && filmsService && !filmsService.ExIdsKey) {
          methods.setValue('ServiceExIDs', [...currentServiceExIds, filmsService.id]);
        } else if (filmsService && filmsService.ExIdsKey && currentServiceExIds) {
          methods.setValue('ServiceExIDs', {
            ...currentServiceExIds,
            [filmsService.ExIdsKey]: [...currentServiceExIds[filmsService.ExIdsKey], filmsService.id],
          });
        }
        methods.setValue('servicesExtra.films', true);
      }

      if (!initService.CanChangeCurrentService && isChangeService) {
        const initialDateFromInit = new DateVOv2({
          dateString: initService.FromDate,
          format: 'yyyy-MM-dd HH:mm:ss',
        })
          .getLuxonKiev()
          .toFormat('dd-MM-yyyy');
        methods.setValue('dateFrom', initialDateFromInit);
      } else {
        setDateFromForCalculation({ initService });
      }

      setIsChangeServicesForCalculation({ initService });
      updateViewState({ initService, userInfo });
      updateCalculation();
    },
    [
      isConnectFilms,
      isConnectStaticIP,
      methods,
      setIsChangeServicesForCalculation,
      updateCalculation,
      setDateFromForCalculation,
      updateViewState,
      isChangeService,
    ],
  );

  useEffect(() => {
    if (isInitError && initError) {
      const message = getErrorMessage(initError);
      history.replace({ pathname: GlobalRouteNames.UnknownError, state: { message } });
    }
  }, [isInitError, initError]);

  useEffect(() => {
    if (!init || !userInfoState) return;
    initialize({ initService: init, userInfo: userInfoState });
  }, [init, initialize, userInfoState]);

  const handleActivationMonthChange = useCallback(
    (date: string) => {
      if (!init || !userInfoState || !date) return;
      const isToday = new DateVOv2({ dateString: date, format: 'dd-MM-yyyy' })
        .getLuxonKiev()
        .hasSame(new DateVOv2().getLuxonKiev(), 'day');

      methods.setValue(
        'dateFrom',
        (init.StatusID === 1 || !!init.Prepaids) && isToday
          ? new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy')
          : date,
      );

      setIsChangeServicesForCalculation({ initService: init });
      updateViewState({ initService: init, userInfo: userInfoState });
      updateCalculation();
    },
    [init, methods, setIsChangeServicesForCalculation, updateCalculation, updateViewState, userInfoState],
  );

  const handleSliderChange = useCallback(
    (number: number | undefined) => {
      const prevousCount = methods.getValues().count;
      methods.setValue('count', number);
      if (prevousCount !== number) {
        updateCalculation();
      }
    },
    [methods, updateCalculation],
  );

  useEffect(() => {
    const currentCount = methods.getValues().count;
    if (currentCount && currentCount > Number(viewState.maxCountMoth)) {
      handleSliderChange(viewState.maxCountMoth);
    }
  }, [handleSliderChange, methods, viewState.maxCountMoth]);

  const onChangeFilms = (e: boolean, srv: ServiceEx) => {
    if (!init || !userInfoState) return;

    isChangeFilms.current = !isChangeFilms.current;
    const currentServiceExIds = methods.getValues().ServiceExIDs;
    if (Array.isArray(currentServiceExIds) && !srv.ExIdsKey) {
      methods.setValue(
        'ServiceExIDs',
        e ? [...currentServiceExIds, srv.id] : currentServiceExIds.filter((i) => i !== srv.id),
      );
    } else if (srv.ExIdsKey && currentServiceExIds) {
      methods.setValue('ServiceExIDs', {
        ...currentServiceExIds,
        [srv.ExIdsKey]: e
          ? [...currentServiceExIds[srv.ExIdsKey], srv.id]
          : currentServiceExIds[srv.ExIdsKey].filter((i: number) => i !== srv.id),
      });
    }

    if (!isChangeService && !isChangeStaticIp.current && init.StatusID === 1)
      setDateFromForCalculation({ initService: init });
    setIsChangeServicesForCalculation({ initService: init });
    updateViewState({ initService: init, userInfo: userInfoState });
    updateCalculation();
  };

  const onChangeDedicatedIp = (e: boolean, srv: ServiceEx) => {
    if (!init || !userInfoState) return;

    isChangeStaticIp.current = !isChangeStaticIp.current;
    const currentServiceExIds = methods.getValues().ServiceExIDs;
    if (Array.isArray(currentServiceExIds) && !srv.ExIdsKey) {
      methods.setValue(
        'ServiceExIDs',
        e ? [...currentServiceExIds, srv.id] : currentServiceExIds.filter((i) => i !== srv.id),
      );
    } else if (srv.ExIdsKey && currentServiceExIds) {
      methods.setValue('ServiceExIDs', {
        ...currentServiceExIds,
        [srv.ExIdsKey]: e
          ? [...currentServiceExIds[srv.ExIdsKey], srv.id]
          : currentServiceExIds[srv.ExIdsKey].filter((i: number) => i !== srv.id),
      });
    }

    if (!isChangeService && !isChangeFilms.current && init.StatusID === 1)
      setDateFromForCalculation({ initService: init });
    setIsChangeServicesForCalculation({ initService: init });
    updateViewState({ initService: init, userInfo: userInfoState });
    updateCalculation();
  };

  const onChangeClientService = (e: boolean, srv: ServiceEx) => {
    if (!init || !userInfoState) return;

    const currentServiceExIds = methods.getValues().ServiceExIDs;
    if (Array.isArray(currentServiceExIds) && !srv.ExIdsKey) {
      methods.setValue(
        'ServiceExIDs',
        e ? [...currentServiceExIds, srv.id] : currentServiceExIds.filter((i) => i !== srv.id),
      );
    } else if (srv.ExIdsKey && currentServiceExIds) {
      methods.setValue('ServiceExIDs', {
        ...currentServiceExIds,
        [srv.ExIdsKey]: e
          ? [...currentServiceExIds[srv.ExIdsKey], srv.id]
          : currentServiceExIds[srv.ExIdsKey].filter((i: number) => i !== srv.id),
      });
    }

    updateCalculation();
  };

  const onChangeUseBonusPoints = useCallback(
    (e: boolean) => {
      methods.setValue('isBonusPayment', e);
      updateCalculation();
    },
    [methods, updateCalculation],
  );

  return (
    <Switch>
      {PaymentRoutes.map((route) => (
        <Route key={route} path={route} exact>
          <div className="page payment">
            <div className="payment__container container">
              <div className="payment__card card card__primary card__primary_full-mobile">
                <PaymentTitle
                  address={userAddress}
                  isSuspension={isPaidServiceSuspension}
                  account={controlledAccount}
                />
                <div className="payment__content">
                  <FormProvider {...methods}>
                    <div className="payment__content__options">
                      <div className="payment__content__options__service-changer">
                        {!isPaidServiceSuspension && (
                          <ServiceInfo
                            internetLogin={internetLogin || null}
                            service={serviceForPayment}
                            userInfoShares={userInfoShares}
                          />
                        )}
                        <ExtraservicesBlock
                          isExtraServicesAvailable={Boolean(userInfoState?.Permissions.ServicesEx.Activate)}
                          ServicesEx={ServicesEx || []}
                          Share={serviceForPayment?.Share}
                          onChangeFilms={onChangeFilms}
                          onChangeDedicatedIp={onChangeDedicatedIp}
                          onChangeClientService={onChangeClientService}
                          isLoading={isLoadingCalculation}
                        />
                      </div>
                      <div className="payment__content__options__activation-period">
                        <FormToggleButtonRefactor
                          name="dateFrom"
                          options={viewState.monthToggle}
                          handleToggleChange={handleActivationMonthChange}
                          isLoading={isLoadingCalculation}
                          isLoaded={!!serviceForPayment}
                        />

                        <RefactorInfoBlock
                          isShare={!!serviceForPayment?.Share}
                          isMonthCountChanged={!!((methods.getValues().count || 1) > 1)}
                          handleChangeCountMonth={handleSliderChange}
                          servicePrice={serviceForPayment?.Price}
                          type={viewState.typeInfoBlock}
                          calculation={calculation}
                          ChangeCurrentServiceInfo={ChangeCurrentServiceInfo}
                          isLoading={isLoadingCalculation}
                        />
                      </div>
                      {viewState.isMonthRangeVisible ? (
                        <MonthRange
                          isBonusPrepaidsAvailable={!!isBonusPrepaidsAvailable}
                          Permissions={Permissions}
                          serviceForPayment={serviceForPayment}
                          maxCountMoth={viewState.maxCountMoth}
                          handleSliderChange={handleSliderChange}
                          calculation={calculation}
                          isPaidServiceSuspension={!!isPaidServiceSuspension}
                          isLoadingCalculation={isLoadingCalculation}
                          сonnectedShare50Percent={сonnectedShare50Percent}
                          isCtv={accountType === AccountTypes.Ctv}
                        />
                      ) : null}
                    </div>
                    <ServicePaymentFormRefactor
                      calculation={calculation}
                      isLoading={isLoadingCalculation}
                      isPaidServiceSuspension={!!isPaidServiceSuspension}
                      maxCountMoth={viewState.maxCountMoth}
                      onChangeUseBonusPoints={onChangeUseBonusPoints}
                      isShowBonusCheckbox={viewState.isShowBonusCheckbox}
                      params={structuredClone(methods.getValues())}
                      init={init}
                      accountType={accountType}
                      Permissions={Permissions}
                      typeInfoBlock={viewState.typeInfoBlock}
                      calculateError={calculateError as unknown as ApiError}
                    />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </Route>
      ))}
      <Route component={Page404} path={GlobalRouteNames.Page404} />
    </Switch>
  );
};
